import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Col, Row, Card, CardBody, CardHeader, Button } from "reactstrap";
import { Fn_AddEditData, Fn_DisplayData, Fn_FillListData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useLocation, useNavigate } from "react-router-dom";
import { Save, ArrowLeft, Truck, Settings } from "react-feather";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const AddEdit_VehicleMaster = () => {
  const API_URL_SAVE = "VehicleMaster/0/token";
  const API_URL_EDIT = API_WEB_URLS.MASTER + "/0/token/VehicleMaster/Id";
  const API_URL_VEHICLE_TYPE = `${API_WEB_URLS.MASTER}/0/token/VehicleTypeMaster/Id/0`;
  
  const breadCrumbTitle = "Vehicle Master";
  const breadcrumbItem = "Vehicle Master";

  const [state, setState] = useState({
    id: 0,
    formData: { 
      F_VehicleType: "",
      Name: "",
      VehicleRegistrationNo: "",
      VehicleNo: "",
      WeightCapacity: "",
    }
  });

  const [vehicleTypeList, setVehicleTypeList] = useState([]);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const nameRef = useRef(null);

  useEffect(() => {
    const Id = (location.state && location.state.Id) || 0;

    if (Id > 0) {
      setState((prevState) => ({ ...prevState, id: Id }));
      Fn_DisplayData(dispatch, setState, Id, API_URL_EDIT);
    }
  }, [dispatch, location.state]);

  // Fetch Vehicle Type list
  useEffect(() => {
    Fn_FillListData(dispatch, setVehicleTypeList, "gridData", API_URL_VEHICLE_TYPE);
  }, [dispatch]);

  // Auto-focus on Name field when component mounts
  useEffect(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  }, []);

  // Validation Schema
  const validationSchema = Yup.object({
    F_VehicleType: Yup.string()
      .required("Vehicle type is required"),
    Name: Yup.string()
      .required("Vehicle name is required")
      .min(2, "Vehicle name must be at least 2 characters")
      .max(100, "Vehicle name must be less than 100 characters"),
    VehicleRegistrationNo: Yup.string()
      .required("Vehicle registration number is required")
      .min(2, "Vehicle registration number must be at least 2 characters")
      .max(100, "Vehicle registration number must be less than 100 characters"),
    VehicleNo: Yup.string()
      .required("Vehicle number is required")
      .min(2, "Vehicle number must be at least 2 characters")
      .max(100, "Vehicle number must be less than 100 characters"),
    WeightCapacity: Yup.number()
      .required("Weight capacity is required")
      .min(0, "Weight capacity must be 0 or greater")
      .max(999999.99, "Weight capacity must be less than 999,999.99"),
  });

  const handleSubmit = async (values) => {
    const obj = JSON.parse(localStorage.getItem("user"));
    let formData = new FormData();
    formData.append("F_VehicleType", values.F_VehicleType || "");
    formData.append("Name", values.Name || "");
    formData.append("VehicleRegistrationNo", values.VehicleRegistrationNo || "");
    formData.append("VehicleNo", values.VehicleNo || "");
    formData.append("WeightCapacity", values.WeightCapacity || "");
    formData.append("UserId", obj === null || obj === undefined ? 0 : obj.uid);

    Fn_AddEditData(
      dispatch,
      setState,
      { arguList: { id: state.id, formData } },
      API_URL_SAVE,
      true,
      "vehicleid",
      navigate,
      "/VehicleMaster"
    );
  };

  return (
    <div className="master-form-container" style={{padding: '10px 0'}}>
      <div className="container-fluid px-2">
        <Breadcrumbs title={breadCrumbTitle} breadcrumbItem={breadcrumbItem} />
        
        {/* Form Card */}
        <Row className="justify-content-center">
          <Col lg={10} md={12} sm={12}>
            <Card className="master-form-card master-form-slide-in">
              <CardHeader className="master-form-card-header" style={{padding: '12px 20px'}}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="form-icon-wrapper me-2">
                      <Settings size={18} color="#6D68CB" />
                    </div>
                    <div>
                      <h5 className="mb-0 fw-bold">Vehicle Master</h5>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardBody className="master-form-card-body">
                <Formik 
                  initialValues={{
                    F_VehicleType: state.formData.F_VehicleType || "",
                    Name: state.formData.Name || "",
                    VehicleRegistrationNo: state.formData.VehicleRegistrationNo || "",
                    VehicleNo: state.formData.VehicleNo || "",
                    WeightCapacity: state.formData.WeightCapacity || "",
                  }} 
                  validationSchema={validationSchema} 
                  onSubmit={handleSubmit}
                  enableReinitialize={true}
                >
                  {({ isSubmitting, errors, touched }) => (
                    <Form>
                      <div className="master-form-grid-2">
                        <div className="form-group mb-3">
                          <label htmlFor="F_VehicleType" className="master-form-label">
                            <Truck size={16} className="me-2" />
                            Vehicle Type <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              as="select"
                              className={`master-form-input ${errors.F_VehicleType && touched.F_VehicleType ? 'is-invalid' : ''}`}
                              name="F_VehicleType"
                            >
                              <option value="">Select Vehicle Type</option>
                              {vehicleTypeList && vehicleTypeList.map((type) => (
                                <option key={type.Id} value={type.Id}>
                                  {type.Name}
                                </option>
                              ))}
                            </Field>
                          </div>
                          <ErrorMessage name="F_VehicleType" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Select the type of vehicle
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="Name" className="master-form-label">
                            <Truck size={16} className="me-2" />
                            Vehicle Name <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              innerRef={nameRef}
                              className={`master-form-input ${errors.Name && touched.Name ? 'is-invalid' : ''}`}
                              type="text"
                              name="Name"
                              placeholder="Enter vehicle name (e.g., Swift, Innova)"
                            />
                            <div className="input-icon">
                              <Truck size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="Name" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter the name of the vehicle
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="VehicleRegistrationNo" className="master-form-label">
                            <Truck size={16} className="me-2" />
                            Vehicle Registration No <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.VehicleRegistrationNo && touched.VehicleRegistrationNo ? 'is-invalid' : ''}`}
                              type="text"
                              name="VehicleRegistrationNo"
                              placeholder="Enter registration number (e.g., MH-01-AB-1234)"
                              style={{ textTransform: 'uppercase' }}
                            />
                            <div className="input-icon">
                              <Truck size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="VehicleRegistrationNo" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter the official registration number of the vehicle
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="VehicleNo" className="master-form-label">
                            <Truck size={16} className="me-2" />
                            Vehicle Number <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.VehicleNo && touched.VehicleNo ? 'is-invalid' : ''}`}
                              type="text"
                              name="VehicleNo"
                              placeholder="Enter vehicle number"
                              style={{ textTransform: 'uppercase' }}
                            />
                            <div className="input-icon">
                              <Truck size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="VehicleNo" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter the vehicle number
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="WeightCapacity" className="master-form-label">
                            <Truck size={16} className="me-2" />
                            Weight Capacity (Kg) <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.WeightCapacity && touched.WeightCapacity ? 'is-invalid' : ''}`}
                              type="number"
                              name="WeightCapacity"
                              placeholder="Enter weight capacity in kg"
                              step="0.01"
                              min="0"
                            />
                            <div className="input-icon">
                              <Truck size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="WeightCapacity" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter the maximum weight capacity in kilograms
                          </div>
                        </div>
                      </div>

                      <div className="master-form-actions mt-3">
                        <div className="d-flex gap-2 justify-content-center">
                          <Button
                            type="button"
                            className="master-form-cancel-btn"
                            onClick={() => navigate("/VehicleMaster")}
                            variant="outline-secondary"
                            size="md"
                          >
                            <ArrowLeft size={16} className="me-1" />
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            className="master-form-submit-btn"
                            disabled={isSubmitting}
                            size="md"
                          >
                            {isSubmitting ? (
                              <div className="master-form-loading me-1" />
                            ) : (
                              <Save size={16} className="me-1" />
                            )}
                            {state.id > 0 ? 'Update Vehicle' : 'Save Vehicle'}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default AddEdit_VehicleMaster;


